import React, { useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popup as KendoPopup } from '@progress/kendo-react-popup';
import cn from 'clsx';
import moment from 'moment';
import styles from './advanced-select.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Button from '@components-clean/core/button';
import { DateTimePickerController } from '@components-clean/core/datetime-picker';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import { Popup } from '@components-clean/core/popup/popup';

import {
    AdvancedSelectProps,
    AdvanceSelectFilter,
    FormValues,
    Operator,
    OperatorItem,
    SelectValue,
    ValueLabel,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import {
    booleanValues,
    boolOperators,
    groupOperators,
    numericAndDateOperators,
    operatorList,
    stringOperators,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.utils';
import {
    FieldDefinitionFieldTypeNumeric,
    PrimitiveValues,
} from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useTranslation } from '@hooks/index';

export const AdvancedSelect = ({
    anchor,
    show,
    dataType,
    anchorAlign,
    popupAlign,
    onCancel,
    onSelect,
    ref,
}: AdvancedSelectProps) => {
    const [value1Disabled, setValue1Disabled] = useState<boolean>(false);
    const [value2Disabled, setValue2Disabled] = useState<boolean>(false);
    const { t } = useTranslation();

    const getOperatorsForDataType = () => {
        switch (dataType) {
            case FieldDefinitionFieldTypeNumeric.Bool:
                return operatorList
                    .filter((x) => boolOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
            case FieldDefinitionFieldTypeNumeric.Text:
            case FieldDefinitionFieldTypeNumeric.CSV:
                return operatorList
                    .filter((x) => stringOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
            case FieldDefinitionFieldTypeNumeric.Int:
            case FieldDefinitionFieldTypeNumeric.Float:
            case FieldDefinitionFieldTypeNumeric.Date:
            case FieldDefinitionFieldTypeNumeric.PartialDate:
            default:
                return operatorList
                    .filter((x) => numericAndDateOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
        }
    };

    const operators = getOperatorsForDataType();

    const {
        control,
        handleSubmit,
        resetField,
        getValues,
        formState: { isValid },
    } = useForm<FormValues>({
        defaultValues: {
            operator1: operators[0],
            value1:
                dataType === FieldDefinitionFieldTypeNumeric.Text || dataType === FieldDefinitionFieldTypeNumeric.CSV
                    ? ''
                    : null,
            operator2: operators[0],
            value2:
                dataType === FieldDefinitionFieldTypeNumeric.Text || dataType === FieldDefinitionFieldTypeNumeric.CSV
                    ? ''
                    : null,
            groupOperator: { value: groupOperators[0].value, label: t(groupOperators[0].label) },
        },
    });

    const handleOnCancel = () => {
        onCancel();
    };

    const mutateCriteria = (
        operator: OperatorItem,
        value: PrimitiveValues | ValueLabel<boolean | null>,
    ): SelectValue => {
        let val = null;

        switch (dataType) {
            case FieldDefinitionFieldTypeNumeric.Bool:
                if (value != null) val = (value as ValueLabel<boolean | null>).value;

                return {
                    operator: operator.value,
                    value: val,
                    dataType,
                };
            case FieldDefinitionFieldTypeNumeric.Date:
            case FieldDefinitionFieldTypeNumeric.PartialDate:
                if (value != null) val = moment(value as Date);

                return {
                    operator: operator.value,
                    value: val,
                    dataType,
                };
            case FieldDefinitionFieldTypeNumeric.Int:
            case FieldDefinitionFieldTypeNumeric.Float:
                if (value != null) val = value as number;

                return {
                    operator: operator.value,
                    value: val,
                    dataType,
                };
            case FieldDefinitionFieldTypeNumeric.Text:
            case FieldDefinitionFieldTypeNumeric.CSV:
                if (value != null) val = value as string;
                return {
                    operator: operator.value,
                    value: val,
                    dataType,
                };
            default:
                throw new Error(`Unsupported data type: ${dataType}`);
        }
    };

    const onSubmit = (data: FormValues) => {
        const filter: AdvanceSelectFilter = {
            logic: data.groupOperator.value,
            filters: [],
        };

        switch (data.operator1.value) {
            case Operator.Null:
            case Operator.NotNull:
                filter.filters.push(mutateCriteria(data.operator1, null));
                break;
            default:
                if (data.value1 != null && data.value1 !== '') {
                    filter.filters.push(mutateCriteria(data.operator1, data.value1));
                }
        }

        switch (data.operator2.value) {
            case Operator.Null:
            case Operator.NotNull:
                filter.filters.push(mutateCriteria(data.operator2, null));
                break;
            default:
                if (data.value2 != null && data.value2 !== '') {
                    filter.filters.push(mutateCriteria(data.operator2, data.value2));
                }
        }

        onSelect(filter);
    };

    const handleOperatorChange = (name: 'operator1' | 'operator2', value: OperatorItem) => {
        let disableValue = false;
        if (value.value === Operator.Null || value.value === Operator.NotNull) {
            resetField(name === 'operator1' ? 'value1' : 'value2');
            disableValue = true;
        }

        if (name === 'operator1') setValue1Disabled(disableValue);
        else setValue2Disabled(disableValue);
    };

    const getComponentForDataType = (
        formControl: Control<FormValues, unknown>,
        name: 'value1' | 'value2',
        type?: FieldDefinitionFieldTypeNumeric,
    ) => {
        const key = name === 'value1' ? getValues('operator1').value : getValues('operator2').value;
        switch (type) {
            case FieldDefinitionFieldTypeNumeric.Int:
            case FieldDefinitionFieldTypeNumeric.Float:
                return (
                    <NumericTextBoxController
                        control={formControl}
                        name={name}
                        data-testid={`advanced-select-${name}`}
                        fillMode="flat"
                        disabled={name === 'value1' ? value1Disabled : value2Disabled}
                        key={`Num${key}`}
                    />
                );
            case FieldDefinitionFieldTypeNumeric.Bool:
                return (
                    <DropDownListController
                        control={formControl}
                        name={name}
                        data-testid={`advanced-select-${name}`}
                        data={booleanValues.map((x) => ({
                            value: x.value,
                            label: t(x.label),
                        }))}
                        textField="label"
                        clearButton={false}
                        fillMode="flat"
                        dataItemKey="value"
                        disabled={name === 'value1' ? value1Disabled : value2Disabled}
                        popupSettings={{ className: 'filter-ui-excluded' }}
                        key={`Bool${key}`}
                    />
                );
            case FieldDefinitionFieldTypeNumeric.Date:
            case FieldDefinitionFieldTypeNumeric.PartialDate:
                return (
                    <DateTimePickerController
                        popup={(popupProps) => {
                            return (
                                <KendoPopup
                                    {...popupProps}
                                    className={cn(popupProps.className, 'filter-ui-excluded')}
                                />
                            );
                        }}
                        control={formControl}
                        name={name}
                        data-testid={`advanced-select-${name}`}
                        fillMode="flat"
                        disabled={name === 'value1' ? value1Disabled : value2Disabled}
                        key={`Date${key}`}
                    />
                );
            case FieldDefinitionFieldTypeNumeric.Text:
            case FieldDefinitionFieldTypeNumeric.CSV:
            default:
                return (
                    <TextBoxController
                        control={formControl}
                        name={name}
                        data-testid={`advanced-select-${name}`}
                        fillMode="flat"
                        disabled={name === 'value1' ? value1Disabled : value2Disabled}
                        maxLength={150}
                        key={`Text${key}`}
                    />
                );
        }
    };

    return (
        <Popup
            data-testid="advanced-select"
            anchor={anchor}
            show={show && dataType != null}
            anchorAlign={anchorAlign}
            popupAlign={popupAlign}
            onBlur={onCancel}
            popupClass="k-rounded-md"
        >
            <form>
                <div ref={ref} className={cn(styles.formContainer, 'k-d-flex k-flex-col')}>
                    <div className={cn(styles.header, 'k-d-flex k-align-items-center')}>
                        <FontAwesomeIcon icon="list-check" size="lg" />
                        <div>{t('advancedSelect')}</div>
                    </div>
                    <div className={cn(styles.body, 'k-d-flex k-flex-col')}>
                        <BasicDropDownController
                            customClassName="filter-ui-excluded"
                            control={control}
                            name="operator1"
                            data-testid="advanced-select-operator1"
                            data={operators}
                            handleInputChange={(event) =>
                                handleOperatorChange('operator1', event.target.value as OperatorItem)
                            }
                            textField="label"
                            fillMode="flat"
                        />
                        {getComponentForDataType(control, 'value1', dataType)}
                        <BasicDropDownController
                            customClassName="filter-ui-excluded"
                            control={control}
                            name="groupOperator"
                            data-testid="advanced-select-groupOperator"
                            data={groupOperators.map((x) => ({
                                value: x.value,
                                label: t(x.label),
                            }))}
                            textField="label"
                            dataItemKey="value"
                            fillMode="flat"
                        />
                        <BasicDropDownController
                            customClassName="filter-ui-excluded"
                            control={control}
                            name="operator2"
                            data-testid="advanced-select-operator2"
                            data={operators}
                            handleInputChange={(event) =>
                                handleOperatorChange('operator2', event.target.value as OperatorItem)
                            }
                            textField="label"
                            fillMode="flat"
                        />
                        {getComponentForDataType(control, 'value2', dataType)}
                    </div>
                    <div className={cn(styles.footer, 'k-d-flex')}>
                        <Button data-testid="advanced-select-cancel" type="reset" onClick={handleOnCancel}>
                            {t('cancel')}
                        </Button>
                        <Button
                            data-testid="advanced-select-confirm"
                            type="submit"
                            primary
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isValid}
                        >
                            {t('select')}
                        </Button>
                    </div>
                </div>
            </form>
        </Popup>
    );
};
